/**
 * ★★ WK — "De onde eles vêm": ROSCA com o total no centro e legenda à direita.
 *
 * ⚠ A primeira versão era pizza cheia, pequena, com nome truncado e o
 * "N fechados" caindo numa linha solta. O dono reprovou. O desenho aprovado é:
 * rosca (o buraco carrega o total), legenda à DIREITA em colunas alinhadas, e
 * uma frase em cinza embaixo dizendo o que chama atenção.
 *
 * ★ SVG puro, sem biblioteca: são sete arcos e o projeto não tem dependência de
 * gráfico. Trazer uma para isto seria caro demais.
 *
 * ★★ AS CORES SÃO DISTINGUÍVEIS, não sete tons do mesmo azul — foi pedido
 * explícito. E **"Sem origem" é sempre CINZA**, em qualquer posição: é ausência
 * de dado, não um canal de captação, e pintá-la de cor a faria parecer um.
 */

const pct1 = (v) => `${Number(v ?? 0).toFixed(1).replace('.', ',')}%`;

/** Paleta do desenho aprovado — matizes distintos, não uma escala. */
const CORES = ['#17418f', '#2563eb', '#60a5fa', '#0f766e', '#a5b4fc', '#b45309', '#7c3aed', '#be123c'];
const CINZA_SEM_ORIGEM = '#98a2b3';

/** Ponto do círculo para um ângulo em voltas (0 = topo, sentido horário). */
function ponto(voltas, raio) {
  const a = 2 * Math.PI * (voltas - 0.25);
  return [Math.cos(a) * raio, Math.sin(a) * raio];
}

export default function PizzaOrigem({ fatias = [], total = 0, destaque = null }) {
  if (!fatias.length || total <= 0) return <p className="em-breve">Nenhum lead no período.</p>;

  const R = 1;
  const r = 0.485; // ★ o buraco da rosca — é ele que carrega o total no centro
  let acumulado = 0;
  let cor = 0;
  const arcos = fatias.map((f) => {
    const parte = (Number(f.leads) || 0) / total;
    const de = acumulado;
    acumulado += parte;
    const semOrigem = !f.id;
    const tom = semOrigem ? CINZA_SEM_ORIGEM : CORES[cor++ % CORES.length];
    const [x1, y1] = ponto(de, R);
    const [x2, y2] = ponto(acumulado, R);
    const [u1, v1] = ponto(acumulado, r);
    const [u2, v2] = ponto(de, r);
    const grande = parte > 0.5 ? 1 : 0;
    // ★ Uma fatia de 100 % não vira arco: início e fim coincidem e o `A` não
    // desenha nada. Com uma origem só, a rosca é um anel inteiro.
    const d = parte >= 0.999
      ? `M 0 ${-R} A ${R} ${R} 0 1 1 -0.0001 ${-R} Z M 0 ${-r} A ${r} ${r} 0 1 0 -0.0001 ${-r} Z`
      : `M ${x1} ${y1} A ${R} ${R} 0 ${grande} 1 ${x2} ${y2} L ${u1} ${v1} A ${r} ${r} 0 ${grande} 0 ${u2} ${v2} Z`;
    return { d, tom, parte, ...f };
  });

  return (
    <>
      <div className="rosca-envolve">
        <div className="rosca-caixa">
          <svg viewBox="-1.02 -1.02 2.04 2.04" className="rosca" role="img" aria-label="Leads por origem">
            {arcos.map((a) => (
              <path key={a.id || 'sem-origem'} d={a.d} fill={a.tom} fillRule="evenodd">
                <title>{`${a.label}: ${a.leads} leads (${pct1(a.parte * 100)}), ${a.fechamentos} fechados`}</title>
              </path>
            ))}
          </svg>
          <div className="rosca-centro">
            <span className="rosca-total num">{total}</span>
            <span className="rosca-legenda-centro">leads</span>
          </div>
        </div>

        <ul className="rosca-legenda">
          {arcos.map((a) => (
            <li key={a.id || 'sem-origem'}>
              <span className="rosca-cor" style={{ background: a.tom }} aria-hidden="true" />
              <span className="rosca-nome">{a.label}</span>
              <span className="rosca-pct num">{pct1(a.parte * 100)}</span>
              <span className="rosca-vol num">{a.leads} · {a.fechamentos} fech.</span>
            </li>
          ))}
        </ul>
      </div>

      {/* ★ A frase do desenho: o que o quadro mostra e ninguém leria sozinho. */}
      {destaque && (
        <p className="rosca-nota">
          <strong>{destaque.maior.label}</strong> traz {pct1(destaque.maior.parte)} do volume
          {destaque.maior.fechamentos > 0 ? ` e fecha ${destaque.maior.fechamentos}` : ' e ainda não fechou nenhum'}.
          {destaque.melhor && (
            <>
              {' '}
              <strong>{destaque.melhor.label}</strong> traz {destaque.melhor.leads} e fecha{' '}
              {destaque.melhor.fechamentos} — a melhor taxa da casa, e a fatia que mal se vê.
            </>
          )}
        </p>
      )}
    </>
  );
}
